import { ReadonlyVec3, vec3 } from 'gl-matrix';
import type { Image3D } from '~/domain/patient/image3d';
import { sharesFrameOfReference } from '~/domain/dicom/uids';
import { petImageOfPair } from '~/domain/patient/petImage';
import type { Environment } from '~/engine/data/Environment';

/** The images an Environment draws, read for the PET among them. */
type DrawnPair = Pick<Environment, 'image' | 'secondaryImage'>;

/**
 * The PET image an Environment draws, or undefined when it draws none: the
 * fused secondary when that is PET, else the Environment's own image when
 * that is PET. The secondary wins because it is the side drawn through the
 * PET colour ramp as the overlay.
 */
export function petImageOf(environment: DrawnPair): Image3D | undefined {
    return petImageOfPair(
        environment.image?.parent,
        environment.secondaryImage?.parent
    );
}

type PlacedPair = Pick<
    Environment,
    'image' | 'secondaryImage' | 'registration'
>;

/**
 * Carries a point (mm) between the row's PET frame and its own image's frame,
 * by the registration as it stands at the call. Null when the Environment
 * draws no PET or the registration cannot place the point.
 */
function carryBetweenPetAndRow(
    environment: PlacedPair,
    point: ReadonlyVec3,
    direction: 'petToRow' | 'rowToPet'
): ReadonlyVec3 | null {
    const pet = petImageOf(environment);
    const rowImage = environment.image?.parent;
    if (!pet || !rowImage) {
        return null;
    }
    if (
        pet === rowImage ||
        sharesFrameOfReference(
            pet.frameOfReferenceUid,
            rowImage.frameOfReferenceUid
        )
    ) {
        return point;
    }
    const [from, into] =
        direction === 'petToRow'
            ? [pet.frameOfReferenceUid, rowImage.frameOfReferenceUid]
            : [rowImage.frameOfReferenceUid, pet.frameOfReferenceUid];
    const transform = environment.registration?.getTransformFromTo(from, into);
    return transform
        ? vec3.transformMat4(vec3.create(), point, transform)
        : null;
}

/** A point (mm) of the row's PET, in the frame of the row's own image. */
export function petPointInRowFrame(
    environment: PlacedPair,
    point: ReadonlyVec3
): ReadonlyVec3 | null {
    return carryBetweenPetAndRow(environment, point, 'petToRow');
}

/** The inverse of {@link petPointInRowFrame}. */
export function rowPointInPetFrame(
    environment: PlacedPair,
    point: ReadonlyVec3
): ReadonlyVec3 | null {
    return carryBetweenPetAndRow(environment, point, 'rowToPet');
}
